import { useMutation } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'

import { SigningOperationCard } from '../../src/features/signing/components/SigningOperationCard'
import { EMPTY_ASSIGNED, EMPTY_INITIATED } from '../../src/features/signing/signingVM'
import { useSigningOperations } from '../../src/features/signing/useSigning'
import { useApp } from '../../src/i18n'
import { useDocumentTitle } from '../../src/lib/useDocumentTitle'
import type { SigningOperation } from '../../src/lib/types'
import { Button, QueryState, Seg, SkeletonLines } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

/**
 * Подписание через SIMA İmza. «Мне на подпись» — операции, адресованные моему сертификату;
 * «Я отправил» — то, что я сам отправил на подпись.
 *
 * Пустой список у «мне на подпись» почти всегда означает, что человек ещё не подтверждал вход
 * через SIMA: сертификат привязывается именно тогда, и до этого адресовать ему нечего.
 * Поэтому вместо пустого экрана — объяснение, что сделать.
 */
export default function SigningScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  const p = useLocalSearchParams<{ tab?: string }>()
  const initiated = p.tab === 'initiated'
  const scope = initiated ? 'initiated' : 'assigned'
  const q = useSigningOperations(scope)
  useDocumentTitle(l('nav.signing'))
  const rows = q.data ?? []
  const [auth, setAuth] = useState<SigningOperation | null>(null)
  // Подтверждение входа — единственный способ привязать сертификат к карточке: до него
  // адресовать человеку документы нечем, и «Мне на подпись» у него всегда пуст.
  const confirmLogin = useMutation({
    mutationFn: () => api.createSigningAuth(),
    onSuccess: (op) => setAuth(op),
  })

  return (
    <Page
      title={l('nav.signing')}
      subtitle={
        initiated
          ? t({ ru: 'Документы, которые вы отправили на подпись', en: 'Documents you sent for signing', az: 'İmzaya göndərdiyiniz sənədlər' })
          : t({ ru: 'Документы, ожидающие вашей подписи', en: 'Documents awaiting your signature', az: 'İmzanızı gözləyən sənədlər' })
      }
      action={
        <XStack gap={6}>
          <Seg
            active={!initiated}
            label={t({ ru: 'Мне на подпись', en: 'For my signature', az: 'İmzam üçün' })}
            badge={!initiated ? rows.length || undefined : undefined}
            onPress={() => router.setParams({ tab: '' })}
          />
          <Seg
            active={initiated}
            icon="send"
            label={t({ ru: 'Я отправил', en: 'Sent by me', az: 'Göndərdiyim' })}
            onPress={() => router.setParams({ tab: 'initiated' })}
          />
        </XStack>
      }
      maxWidth={760}
    >
      <YStack gap={12} marginBottom={16}>
        <Button
          disabled={confirmLogin.isPending}
          opacity={confirmLogin.isPending ? 0.5 : 1}
          onPress={() => confirmLogin.mutate()}
        >
          {t({
            ru: 'Подтвердить вход через SIMA',
            en: 'Confirm login with SIMA',
            az: 'SIMA ilə girişi təsdiqlə',
          })}
        </Button>
        {auth ? <SigningOperationCard op={auth} /> : null}
      </YStack>

      <QueryState q={q}>
        {q.isLoading ? (
          <SkeletonLines lines={3} />
        ) : rows.length === 0 ? (
          <Text fontSize={14} color="$muted" lineHeight={22}>
            {t(initiated ? EMPTY_INITIATED : EMPTY_ASSIGNED)}
          </Text>
        ) : (
          <YStack gap={12}>
            {rows.map((op) => (
              <SigningOperationCard key={op.id} op={op} />
            ))}
          </YStack>
        )}
      </QueryState>
    </Page>
  )
}
